import { Css, useTestIds } from "@homebound/beam";
import { emptyCellDash } from "src/components/gridTableCells";
import { Maybe } from "src/generated/graphqlTypes";
import { formatNumberToString, isDefined } from "src/utils";
import { Icon } from "./Icon";

export type PercentageProps = {
  id?: string;
  percent: Maybe<number>;
  dropZero?: boolean;
  basisPoints?: boolean;
  /** Value to compare against the main percentage, used to display a difference if present */
  comparedValuePercent?: Maybe<number>;
  /** Makes positive numbers red and negative colors green for the compared value difference */
  invertComparedValueColors?: boolean;
  /** Makes the percentage number green if above, red if below */
  desiredPercentage?: Maybe<number>;
  numFractionDigits?: number;
};

export function Percentage(props: PercentageProps) {
  const {
    id = "percentage",
    percent,
    dropZero = false,
    basisPoints = false,
    comparedValuePercent,
    invertComparedValueColors = false,
    numFractionDigits,
  } = props;
  const testId = useTestIds({}, id);
  const formattedPercentage =
    typeof percent !== "number" || (dropZero && percent === 0)
      ? emptyCellDash
      : `${formatPercent(basisPoints ? percent / 100 : percent, numFractionDigits)}%`;
  const comparedValue = percent && comparedValuePercent ? percent - comparedValuePercent : undefined;
  const isComparedValueNegative = (!!comparedValue && comparedValue < 0) !== invertComparedValueColors;
  const formattedComparedValue = comparedValue ? `${formatNumberToString(comparedValue, true)}%` : undefined;

  const colorStyle = getColorStyle(props);

  return (
    <>
      <span {...testId} css={colorStyle}>
        {formattedPercentage}
      </span>
      {comparedValue && (
        <span
          {...testId.comparedValue}
          css={Css.df.xsSb.gapPx(3).fdr.ml1.if(isComparedValueNegative).red500.else.green500.$}
        >
          {formattedComparedValue}
          <div css={Css.mya.$}>
            <Icon icon={isComparedValueNegative ? "arrowDown" : "arrowUp"} inc={1.8} />
          </div>
        </span>
      )}
    </>
  );
}

function getColorStyle({ percent, desiredPercentage }: PercentageProps) {
  return isDefined(percent) && percent < (desiredPercentage ?? 0)
    ? Css.red500.$
    : isDefined(desiredPercentage)
      ? Css.green500.$
      : Css.$;
}

function formatPercent(percent: number, numFractionDigits: number | undefined): string {
  return isDefined(numFractionDigits)
    ? percent.toLocaleString(undefined, {
        minimumFractionDigits: numFractionDigits,
        maximumFractionDigits: numFractionDigits,
      })
    : formatNumberToString(percent, true);
}
